import React, { useMemo } from "react";
import styled from "styled-components";
import { Container, Card, Form, Spinner, Badge } from "react-bootstrap";
import { useTask } from "../../../providers";
import { ActivityItem } from "./ActivityItem";
import { ActivityLogLevel, TaskStatus } from "../../../interfaces";
import {
  failureReasonBadge,
  failureReasonLabel,
} from "../../../interfaces/failure-reasons";
import { formatDuration, intervalToDuration } from "date-fns";
import { RunTypeBadge } from "../run-type-badge";

const TaskContainer = styled(Container)`
  padding: 20px;
`;

const TaskHeader = styled(Card)`
  margin-bottom: 20px;
`;

const TaskHeaderContent = styled(Card.Body)`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TaskInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const TaskTitle = styled.h4`
  margin-bottom: 8px;
`;

const TaskMeta = styled.div`
  display: flex;
  gap: 15px;
`;

const TaskControls = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const LogLevelSelect = styled(Form.Select)`
  width: 200px;
`;

const ResultSection = styled(Card)`
  margin-top: 20px;
  margin-bottom: 20px;
`;

const ResultHeader = styled(Card.Header)`
  background-color: #f8f9fa;
  font-weight: bold;
`;

const CouponResultItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
`;

const CouponCode = styled.div<{ isBest?: boolean }>`
  font-family: monospace;
  font-weight: ${(props) => (props.isBest ? "bold" : "normal")};
  padding: 3px 8px;
  background-color: ${(props) => (props.isBest ? "#e7f5ff" : "#f8f9fa")};
  border-radius: 4px;
  margin-right: 15px;
  width: 150px;
`;

const CouponStatus = styled.div`
  margin-right: 15px;
  width: 100px;
`;

const CouponSavings = styled.div<{ isPositive?: boolean }>`
  font-weight: ${(props) => (props.isPositive ? "bold" : "normal")};
  color: ${(props) => (props.isPositive ? "green" : "inherit")};
`;

const ActivitiesContainer = styled.div`
  margin-top: 20px;
`;

const NoActivities = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const getStatusVariant = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.PENDING:
      return "warning";
    case TaskStatus.IN_PROGRESS:
      return "primary";
    case TaskStatus.COMPLETED:
      return "success";
    case TaskStatus.FAILED:
      return "danger";
    default:
      return "secondary";
  }
};

export const TaskContent: React.FC = () => {
  const {
    loading,
    task,
    activities,
    activitiesLoading,
    minLogLevel,
    setMinLogLevel,
    autoRefresh,
    setAutoRefresh,
  } = useTask();
  const timespan = useMemo(() => {
    if (!task?.result) return "";
    const started = new Date(task.result.startedAt);
    const finished = new Date(task.result.finishedAt);
    const duration = intervalToDuration({ start: started, end: finished });
    return formatDuration(duration, {
      format: ["hours", "minutes", "seconds"],
    });
  }, [task?.result?.startedAt, task?.result?.finishedAt]);

  const handleLogLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMinLogLevel(e.target.value as ActivityLogLevel);
  };

  if (loading) {
    return (
      <LoadingSpinner>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </LoadingSpinner>
    );
  }

  if (!task) {
    return <div>Task not found</div>;
  }

  return (
    <TaskContainer fluid>
      <TaskHeader>
        <TaskHeaderContent>
          <TaskInfo>
            <TaskTitle>Task for {task.domain}</TaskTitle>
            <TaskMeta>
              <div>
                Status:{" "}
                <Badge bg={getStatusVariant(task.status)}>{task.status}</Badge>
              </div>
              <div>Country: {task.countryCode}</div>
              {task.merchant && <div>Merchant: {task.merchant.name}</div>}
              <div>
                Run Type:{" "}
                <RunTypeBadge runType={task.runType} />
              </div>
              <div>LLM Cost: ${task.llmTotalCost?.toFixed(4) || "0.0000"}</div>
              <div>
                Vision Cost: ${task.visionTotalCost?.toFixed(4) || "0.0000"} (
                {task.visionCallCount || 0} calls)
              </div>
              {task.status === TaskStatus.COMPLETED && (
                <>
                  <div>
                    Outcome:{" "}
                    {task.ranSuccessfully === true ? (
                      <Badge bg="success">Success</Badge>
                    ) : task.ranSuccessfully === false ? (
                      <Badge bg="danger">Failed</Badge>
                    ) : (
                      <Badge bg="secondary">Unknown</Badge>
                    )}
                  </div>
                  {task.requiredHumanIntervention && (
                    <div>
                      <Badge bg="warning">Required Human Intervention</Badge>
                    </div>
                  )}
                </>
              )}
            </TaskMeta>
          </TaskInfo>
          <TaskControls>
            <Form.Group>
              <Form.Label>Minimum Log Level</Form.Label>
              <LogLevelSelect
                value={minLogLevel}
                onChange={handleLogLevelChange}
              >
                <option value={ActivityLogLevel.DEBUG}>Debug</option>
                <option value={ActivityLogLevel.INFO}>Info</option>
                <option value={ActivityLogLevel.WARN}>Warning</option>
                <option value={ActivityLogLevel.ERROR}>Error</option>
              </LogLevelSelect>
            </Form.Group>
            <Form.Group>
              <Form.Check
                type="switch"
                id="auto-refresh-switch"
                label="Auto Refresh"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            </Form.Group>
          </TaskControls>
        </TaskHeaderContent>
      </TaskHeader>

      {(task.errorMessage || task.status === TaskStatus.FAILED) && (
        <ResultSection>
          <ResultHeader>Error Message</ResultHeader>
          <Card.Body>
            {task.errorType && (
              <Badge
                bg={failureReasonBadge(task.errorType).bg}
                text={failureReasonBadge(task.errorType).text as any}
                className="mb-2"
              >
                {failureReasonLabel(task.errorType)}
              </Badge>
            )}
            <div className="alert alert-danger mb-0">{task.errorMessage}</div>
          </Card.Body>
        </ResultSection>
      )}

      {task.result && (
        <ResultSection>
          <ResultHeader>Testing Results</ResultHeader>
          <Card.Body>
            <div className="mb-3">
              <strong>Testing Period:</strong>{" "}
              {new Date(task.result.startedAt).toLocaleString()} -{" "}
              {new Date(task.result.finishedAt).toLocaleString()}{" "}
              <b>
                <i>({timespan})</i>
              </b>
            </div>
            <div className="mb-3">
              <strong>Original Total:</strong> {task.result.currency}{" "}
              {task.result.originalTotal.toFixed(2)}
            </div>
            <div className="mb-3">
              <strong>Best Coupon:</strong> {task.result.bestCoupon}
            </div>

            <h5 className="mt-4 mb-3">Tested Coupons</h5>
            {task.result.appliedCoupons.map((coupon, index) => (
              <CouponResultItem key={index}>
                <CouponCode isBest={coupon.code === task.result?.bestCoupon}>
                  {coupon.code}
                </CouponCode>
                <CouponStatus>
                  {coupon.notApplicableToBasket ? (
                    <Badge bg="info">Not Applicable</Badge>
                  ) : coupon.applied ? (
                    <Badge bg="success">Applied</Badge>
                  ) : (
                    <Badge bg="danger">Failed</Badge>
                  )}
                </CouponStatus>
                <CouponSavings isPositive={coupon.savings > 0}>
                  {coupon.notApplicableToBasket ? (
                    <>
                      Not applicable to basket: <b>{coupon.errorMessage}</b>
                    </>
                  ) : coupon.savings > 0 ? (
                    `Saved ${task.result?.currency} ${coupon.savings.toFixed(
                      2
                    )}${
                      coupon.percentageSavings
                        ? ` (${coupon.percentageSavings.toFixed(1)}%)`
                        : ""
                    }`
                  ) : (
                    <b>{coupon.errorMessage || "No savings"}</b>
                  )}
                </CouponSavings>
              </CouponResultItem>
            ))}
          </Card.Body>
        </ResultSection>
      )}

      <ActivitiesContainer>
        {activitiesLoading ? (
          <LoadingSpinner>
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading activities...</span>
            </Spinner>
          </LoadingSpinner>
        ) : activities.length > 0 ? (
          activities.map((activity) => (
            <ActivityItem
              key={activity._id}
              activity={activity}
              taskId={task._id}
              minLogLevel={minLogLevel}
            />
          ))
        ) : (
          <NoActivities>No activities recorded for this task yet.</NoActivities>
        )}
      </ActivitiesContainer>
    </TaskContainer>
  );
};
